import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SBLogo } from "../components/WebLayout";
import { getAccessToken } from "../lib/supabaseClient";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { CheckCircle2, Circle, ArrowLeft, Loader2, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Internal-only review checklist (design/copy/prototype milestones).
const MILESTONES = [
  { done: true, label: "Design system locked (colors, typography, cards)" },
  { done: true, label: "Landing page copy approved" },
  { done: true, label: "Scout waitlist copy approved" },
  { done: true, label: "Operator waitlist copy approved" },
  { done: true, label: "Creator landing copy approved" },
  { done: true, label: "Business request form copy approved" },
  { done: true, label: "Turnstile bot protection wired on all 4 public forms" },
  { done: true, label: "Legal pages published (Privacy / Terms / FAQ / Contact)" },
  { done: false, label: "Attorney review of legal copy completed" },
  { done: false, label: "Resend production API key configured + delivery test passed" },
  { done: false, label: "Referral formulas drafted (sample values only)" },
  { done: false, label: "Third-party vendor list reviewed" },
];

async function authedFetch(path, opts = {}) {
  const token = await getAccessToken();
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token || ""}` },
  });
}

export default function LaunchReadiness() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await authedFetch("/api/admin/launch-readiness");
      if (!r.ok) { nav("/admin/login"); return; }
      const d = await r.json();
      setData(d);
      setEdit(Object.fromEntries(d.categories.map((c) => [c.category, c.target])));
      setLoading(false);
    })();
  }, [nav]);

  async function saveTargets() {
    setSaving(true);
    try {
      const r = await authedFetch("/api/admin/launch-readiness/targets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(edit),
      });
      if (!r.ok) throw new Error();
      toast.success("Targets updated");
      const rr = await authedFetch("/api/admin/launch-readiness");
      const fresh = await rr.json();
      setData(fresh);
      setEdit(Object.fromEntries(fresh.categories.map((c) => [c.category, c.target])));
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  const CIRC = 2 * Math.PI * 90;

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#080B0D] text-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#A1A1AA]"><Loader2 size={18} className="animate-spin text-[#FFC107]" /> Loading readiness…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080B0D] text-white">
      <Seo title="Launch Readiness" description="SpotBounty internal launch readiness dashboard." path="/admin/launch-readiness" noindex />
      <header className="border-b border-[#2A2F34]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/"><SBLogo size={18} /></Link>
          <Link to="/admin" className="text-[13px] text-[#A1A1AA] hover:text-white flex items-center gap-1.5"><ArrowLeft size={14} /> Admin</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="sb-card p-3 border-[#EF4444]/40 text-[11.5px] text-[#A1A1AA]" style={{ background: "rgba(239,68,68,.04)" }}>
          Internal owner-only view — targets and progress numbers must never be shown publicly.
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_2fr] gap-8 items-center">
          <div className="sb-card p-8 flex flex-col items-center">
            <div className="sb-eyebrow">Readiness</div>
            <div className="relative mt-4">
              <svg width={220} height={220} viewBox="0 0 220 220">
                <circle cx="110" cy="110" r="90" stroke="#2A2F34" strokeWidth="14" fill="none" />
                <circle
                  cx="110" cy="110" r="90" stroke="#FFC107" strokeWidth="14" fill="none"
                  strokeLinecap="round"
                  strokeDasharray={CIRC}
                  strokeDashoffset={CIRC - (CIRC * data.overall) / 100}
                  transform="rotate(-90 110 110)"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-[52px] font-black text-[#FFC107]" data-testid="lr-overall">{data.overall}%</div>
                <div className="sb-eyebrow mt-1">Overall</div>
              </div>
            </div>
          </div>

          <div>
            <div className="sb-eyebrow">Launch Readiness</div>
            <h1 className="sb-headline mt-3 text-[40px]">SPOTBOUNTY LAUNCH READINESS — <span className="text-[#FFC107]">{data.overall}%</span></h1>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {data.categories.map((c) => {
                const over = c.actual >= c.target;
                return (
                  <div key={c.category} className="sb-card p-5" data-testid={`lr-card-${c.category}`}>
                    <div className="flex items-center justify-between">
                      <div className="sb-eyebrow" style={{ fontSize: 10 }}>{c.category}</div>
                      <span className={`sb-pill ${over ? "sb-pill-green" : "sb-pill-amber"}`} style={{ fontSize: 10 }}>{over ? "Target Hit" : "In Progress"}</span>
                    </div>
                    <div className="mt-3 flex items-end gap-2">
                      <div className="text-[26px] font-black">{c.actual}</div>
                      <div className="text-[13px] text-[#A1A1AA] mb-1">/ {c.target} target</div>
                    </div>
                    <div className="mt-3 sb-progress-track">
                      <div className="sb-progress-fill" style={{ width: `${c.pct}%`, background: over ? "#22C55E" : "#FFC107" }} />
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <label className="sb-label" style={{ fontSize: 9 }}>Target</label>
                      <input
                        type="number"
                        className="sb-input"
                        style={{ padding: "6px 10px", maxWidth: 100 }}
                        value={edit[c.category] ?? c.target}
                        onChange={(e) => setEdit({ ...edit, [c.category]: parseInt(e.target.value || "0", 10) })}
                        data-testid={`lr-target-${c.category}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5">
              <button onClick={saveTargets} disabled={saving} data-testid="lr-save" className="sb-btn-primary text-[12px]" style={{ padding: "10px 16px" }}>
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Targets
              </button>
            </div>
          </div>
        </div>

        <div className="mt-10 sb-card p-6">
          <div className="sb-eyebrow">Phase 0 Milestones (Internal Checklist)</div>
          <div className="mt-4 divide-y divide-[#2A2F34]">
            {MILESTONES.map((m, i) => (
              <div key={i} className="py-3 flex items-center gap-3">
                {m.done ? <CheckCircle2 size={16} className="text-[#22C55E]" /> : <Circle size={16} className="text-[#52525b]" />}
                <span className={`text-[13px] ${m.done ? "text-white" : "text-[#A1A1AA]"}`}>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
